import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import './GameTesting.scss';
import { fetchReports, resetAdminReports, resetAdminReportsThunk, updateReportStatus, setSelectedReport as setReduxSelectedReport, deleteReport } from '../../store/slices/gameTestingSlice';
import EditGameReviewModal from './components/EditGameReviewModal';
import ViewGameReviewModal from './components/ViewGameReviewModal';
import BugReportForm from './components/BugReportForm';
import BugReportSummaryTable from './components/BugReportSummaryTable';
import BugDetailsModal from './components/BugDetailsModal';
import ReportDetailView from './components/ReportDetailView';

const GameTesting = () => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const { 
    reports, 
    loading, 
    error,
    lastVisible,
    hasMore
  } = useSelector(state => state.gameTesting);
  
  const [selectedReport, setSelectedReport] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    weekRange: '',
    dateRange: {
      start: '',
      end: ''
    },
    searchTerm: ''
  });

  useEffect(() => {
    // Reset the reports list when component mounts
    dispatch(resetAdminReports());
    dispatch(resetAdminReportsThunk());
    
    // Fetch initial reports for the current user
    dispatch(fetchReports({ 
      itemsPerPage: 10,
      lastVisible: null,
      filters: {},
      userOnly: true // This flag will indicate to fetch only user's reports
    }));
  }, [dispatch]);

  const loadMoreReports = () => {
    if (!hasMore || loading) return;
    
    setLoadingMore(true);
    dispatch(fetchReports({ 
      itemsPerPage: 10,
      lastVisible,
      filters,
      userOnly: true
    })).finally(() => {
      setLoadingMore(false);
    });
  };

  const applyFilters = () => {
    // Reset the reports list first
    dispatch(resetAdminReports());
    dispatch(resetAdminReportsThunk());
    
    // Then fetch with filters
    dispatch(fetchReports({ 
      itemsPerPage: 10,
      lastVisible: null,
      filters: {
        status: filters.status,
        weekRange: filters.weekRange
      },
      userOnly: true
    }));
  };

  const handleStatusUpdate = async () => {
    if (!selectedReport || !newStatus) return;

    try {
      await dispatch(updateReportStatus({
        reportId: selectedReport.id,
        newStatus,
        user: user
      })).unwrap();
      
      setShowViewModal(false);
      setNewStatus('');
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDeleteReport = async (reportId) => {
    try {
      await dispatch(deleteReport(reportId)).unwrap();
      // Close modal - Redux state is automatically updated by deleteReport action
      setShowViewModal(false);
      setSelectedReport(null);
    } catch (error) {
      console.error('Error deleting report:', error);
      // Could show an error message to user
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      status: '',
      weekRange: '',
      dateRange: {
        start: '',
        end: ''
      },
      searchTerm: ''
    });
    
    // Reset the reports list
    dispatch(resetAdminReports());
    dispatch(resetAdminReportsThunk());
    
    // Fetch without filters
    dispatch(fetchReports({ 
      itemsPerPage: 10,
      lastVisible: null,
      filters: {},
      userOnly: true
    }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_testing: { color: 'warning', text: 'Pending Testing' },
      test_report_available: { color: 'info', text: 'Test Report Available' },
      assigned_to_developer: { color: 'primary', text: 'Assigned to Developer' },
      tested_and_resolved: { color: 'success', text: 'Tested and Resolved' }
    };

    const config = statusConfig[status] || { color: 'secondary', text: status };
    return <span className={`status-badge ${config.color}`}>{config.text}</span>;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      let date;
      
      // Handle Firebase timestamp objects
      if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      }
      // Handle regular Date objects
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      // Handle timestamp strings/numbers
      else {
        date = new Date(timestamp);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return format(date, 'MMM d, yyyy h:mm a');
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'Invalid Date';
    }
  };

  // Filter reports by search term
  const filteredReports = reports.filter(report => {
    if (!filters.searchTerm) return true;
    
    const searchTerm = filters.searchTerm.toLowerCase();
    return (
      (report.weekRange?.toLowerCase()?.includes(searchTerm)) ||
      (report.selectedGames?.some(game => game.toLowerCase().includes(searchTerm)))
    );
  });

  // Get unique week ranges for filter
  const weekRanges = [...new Set(reports.map(report => report.weekRange))].sort().reverse();

  if (loading && !loadingMore && reports.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading testing reports...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Error loading reports: {error}</p>
        <button onClick={() => dispatch(fetchReports({ itemsPerPage: 10, userOnly: true }))}>Retry</button>
      </div>
    );
  }

  return (
    <div className="game-testing-container">
      <section className="page-heading">
        <div className="d-flex justify-content-between align-items-center">
          <h3 className="mb-0">My Game Testing Reports</h3>
        </div>
      </section>
      
      <div className="game-testing-content">
        <div className="filters-section">
          <div className="filters-row">
            <div className="filter-item">
              <label>Status:</label>
              <select 
                value={filters.status} 
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="pending_testing">Pending Testing</option>
                <option value="test_report_available">Test Report Available</option>
                <option value="assigned_to_developer">Assigned to Developer</option>
                <option value="tested_and_resolved">Tested and Resolved</option>
              </select>
            </div>
            
            <div className="filter-item">
              <label>Week Range:</label>
              <select 
                value={filters.weekRange} 
                onChange={(e) => handleFilterChange('weekRange', e.target.value)}
              >
                <option value="">All Weeks</option>
                {weekRanges.map(week => (
                  <option key={week} value={week}>
                    {week}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="filter-item">
              <label>Search:</label>
              <input 
                type="text" 
                value={filters.searchTerm} 
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                placeholder="Search by week or game"
              />
            </div>
            
            <button 
              className="filter-button" 
              onClick={applyFilters}
              disabled={loading}
            >
              Apply Filters
            </button>
            
            <button 
              className="reset-button" 
              onClick={resetFilters}
              disabled={loading}
            >
              Reset
            </button>
          </div>
        </div>
        
        <div className="reports-table">
          <table>
            <thead>
              <tr>
                <th>Week Range</th>
                <th>Status</th>
                <th>Games</th>
                <th>Submitted</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredReports.length > 0 ? (
                filteredReports.map(report => (
                  <tr key={report.id}>
                    <td>{report.weekRange || 'N/A'}</td>
                    <td>{getStatusBadge(report.status)}</td>
                    <td>
                      <div className="games-list">
                        {report.selectedGames?.length > 0 
                          ? report.selectedGames.join(', ') 
                          : 'None selected'}
                      </div>
                    </td>
                    <td>{formatDate(report.createdAt)}</td>
                    <td>{formatDate(report.lastUpdated)}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="view-button"
                          onClick={() => {
                            setSelectedReport(report);
                            setShowViewModal(true);
                          }}
                        >
                          View
                        </button>
                        <button 
                          className="edit-button"
                          onClick={() => {
                            setSelectedReport(report);
                            dispatch(setReduxSelectedReport(report));
                            setShowEditModal(true);
                          }}
                        >
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="no-reports">
                    No reports found. Adjust filters or submit a new report.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          
          {hasMore && reports.length > 0 && (
            <div className="load-more">
              <button 
                className="load-more-button"
                onClick={loadMoreReports}
                disabled={loading || loadingMore}
              >
                {loadingMore ? (
                  <>
                    <span className="spinner"></span>
                    Loading more...
                  </>
                ) : (
                  'Load More'
                )}
              </button>
            </div>
          )}
          
          {loading && (
            <div className="loading-more">
              <div className="loading-spinner-small"></div>
              <span>Loading more reports...</span>
            </div>
          )}
        </div>
        
        {showViewModal && selectedReport && (
          <div className="modal-overlay">
            <ReportDetailView
              report={selectedReport}
              onClose={() => {
                setShowViewModal(false);
                setSelectedReport(null);
              }}
              onStatusUpdate={handleStatusUpdate}
              onDelete={handleDeleteReport}
            />
          </div>
        )}
        
        {showEditModal && selectedReport && (
          <EditGameReviewModal
            show={showEditModal}
            onHide={() => {
              setShowEditModal(false);
              setSelectedReport(null);
            }}
            report={selectedReport}
          />
        )}
      </div>
    </div>
  );
};

export default GameTesting;
export { 
  EditGameReviewModal, 
  ViewGameReviewModal, 
  BugReportForm, 
  BugReportSummaryTable, 
  BugDetailsModal 
}; 